import { type ButtonProps } from '@atlaskit/button/new';
import { type FlagProps } from '@atlaskit/flag';
import {
  type ModalDialogProps,
  type ModalTitleProps,
  type ModalHeaderProps,
  type ModalBodyProps,
  type ModalFooterProps,
} from '@atlaskit/modal-dialog';
import { type token, type ThemeState } from '@atlaskit/tokens';
import type {
  CrossFlowContextType,
  CompletionStatus as OpenCrossFlowResult,
} from '@atlassiansox/cross-flow-support';
import { type ReactElement } from 'react';
import { type PartialDeep } from 'type-fest';

import {
  type BoardId,
  type ContentId,
  type DocumentTransformerCallback,
  type DocumentTransformerFormat,
} from '@canvas/model/board';
import { type ANONYMOUS_ID } from '@canvas/model/identity';
import { type SpotlightScreenType, type SpotlightScreen } from '@canvas/model/onboarding';
import {
  type EntitlementFeatureBody,
  type PaywallFeatureStateFromEntitlement,
} from '@canvas/paywall-types';
import { type StringWithAutocomplete } from '@canvas/type-utils';

export interface FrameContainer {
  // Container (space) key https://support.atlassian.com/confluence-cloud/docs/choose-a-space-key/
  key: string;
  name: string;
  source: string;
}

export enum UserTypes {
  // Logged in, part of Confluence
  CONFLUENCE_USER = 'confluence-user',
  // Logged in, part of the org
  ORG_MEMBER = 'org-member',
  // Logged in, not part of the org
  ATLASSIAN_USER = 'atlassian-user',
  // Not logged in
  ANONYMOUS = 'anonymous',
}

// The raw values we get can get from the content API
export interface RawWhiteboardSessionData {
  accountId: string | typeof ANONYMOUS_ID | null;
  tenantId: string | null | undefined;
  activationId: string | null | undefined;
  publicLinkUserType: UserTypes;
}

export interface WhiteboardSessionData {
  accountId: string;
  tenantId: string;
  activationId: string;
  publicLinkUserType: UserTypes;
}

export interface WhiteboardQueryParams {
  openTemplatePicker?: boolean;
}

export interface Collaborator {
  agentId?: string;
  agentOwnerName?: string;
  agentType?: string;
  userId: string;
  clientId: number;
  name: string;
  color: string;
  onClick?: () => void;
  followCursor?: boolean;
  kind?: 'user' | 'rovo-agent' | 'external-agent';
  avatar: {
    src?: string;
  };
}

export interface Collaborators {
  collaborators: Collaborator[];
  me: Collaborator;
}
export interface MainAction {
  id: string;
  text?: string;
  icon?: string;
  tooltipText?: string;
  onClick?: () => void;
  expanded?: boolean;
  appearance?: string;
  isSelected?: boolean;
  styles?: {
    [prop: string]: string;
  };
}

export interface MainActions {
  mainActions: MainAction[];
}

export interface Item {
  id: string;
  text: string;
  shortcutText?: string;
  onClick?: () => void;
  url?: string;
  target?: string;
}

export interface ItemGroup {
  id: string;
  items: Item[];
  text?: string;
}

export interface MoreActions {
  items: (Item | ItemGroup)[][];
}

export interface BoardStatus {
  status: string;
  tooltipText?: string;
}

/**
 * Progress signal reported opportunistically by Canvas/Databases iframe surfaces
 * so Confluence can decide when to show the "Flag to Share" onboarding nudge
 * (TWCG-2055). Canvas reports raw counts only; Confluence owns the threshold,
 * inactivity timer, experiment eligibility, and rendering.
 */
export type WhiteboardShareProgress = { type: 'whiteboard'; elements: number };
export type DatabaseShareProgress = {
  type: 'database';
  rows: number;
  columns: number;
  filledCells: number;
};
export type ShareCriteriaProgress = WhiteboardShareProgress | DatabaseShareProgress;

export type EditionUpgradeCTAProps = {
  title: string;
  body: string | ReactElement;
  feature: string;
  image: {
    src: {
      default: string;
      dark: string;
    };
    alt: string;
  };
  learnMoreLink: {
    href: string;
    message: string;
  };
};

export type ResolveWhiteboardCommentIds = {
  contentId: string | undefined;
  referenceId: string | undefined;
};

export type ResolvedCommentHandler = (resolvedIds: ResolveWhiteboardCommentIds) => void;
export type CopyAllToClipboardHandler = () => void;

export type WhiteboardInternalModalProps = {
  modalHeader: {
    title: {
      content: string;
      props?: Omit<ModalTitleProps, 'children'>;
    };
    props?: Omit<ModalHeaderProps, 'children'>;
  };
  modalBody: {
    content: string;
    props?: Omit<ModalBodyProps, 'children'>;
  };
  modalFooter: {
    buttons: {
      label: string;
      shouldCloseModal?: boolean;
      props?: Omit<ButtonProps, 'children'>;
    }[];
    props?: Omit<ModalFooterProps, 'children'>;
  };
  props?: Omit<ModalDialogProps, 'children'>;
  image?: string;
};

export interface WhiteboardInternalModalConfiguration {
  type: 'dialog' | 'dialogList';
}

export interface WhiteboardDialogConfiguration
  extends WhiteboardInternalModalConfiguration, WhiteboardInternalModalProps {
  type: 'dialog';
}

export interface WhiteboardOnboardingDialogListConfiguration extends WhiteboardInternalModalConfiguration {
  type: 'dialogList';
  dialogList: WhiteboardInternalModalProps[];
  props?: Omit<ModalDialogProps, 'children'>;
  bodyTextHeight?: string;
}

export type OnboardingStartHandler = (initialSpotlightScreen?: string) => void;

/**
 * Event that can be triggered in Confluence Frontend
 * Note: This should ideally be replaced with a type library from Confluence Frontend
 */
export type ConfluenceEvent = 'copy' | 'hideSidePanel' | StringWithAutocomplete;
export type ConfluenceEventHandler = () => void;

// 'Flag' refers to Atlaskit's <Flag>, NOT feature flags!

// if adding more icons, ensure updating the type in AFM as well
export type ContentTypesFlagIconString = 'feedback' | 'info' | 'mention' | 'avatar';

// NOTE: This is directly lifted from Confluence's 'flags' package and should be kept in sync
type FlagType = 'success-circle' | 'success' | 'info' | 'error' | 'warning' | 'custom';

// We omit the 'icon' since we currently cannot pass JSX through the bridge API, plus if we specify a
// type other than custom then this gets handled by confluence automagically. We also specify
// the 'id' and 'key' manually in AFM since Whiteboards should not have to worry about these.
// For docs on atlaskit flag: https://atlassian.design/components/flag/examples

// Aside: This should really be sourced from @confluence/flag's FlagDescriptor type, but this is
// connie-specific, so we instead reference the shared @atlaskit/flag type and translate this to
// what we need in Connie at the CFE level
// Annoyingly, due to type mismatches between @atlaskit/flag and @confluence/flags, the latter doesn't
// specify onDismissed, opting instead for onClose, hence we must omit it too and replace it with onClose
type BaseFlagProps = Omit<FlagProps, 'icon' | 'key' | 'onDismissed'> & {
  onClose?: VoidFunction;
  close?: 'auto' | 'never';
};

interface StandardFlagProps extends BaseFlagProps {
  type: Exclude<FlagType, 'custom'>;
}

type CustomFlagProps = BaseFlagProps &
  (
    | {
        type: 'custom';
        iconString: Exclude<ContentTypesFlagIconString, 'avatar' | 'mention'>;
        iconColor?: ReturnType<typeof token>;
      }
    | {
        type: 'custom';
        iconString: 'mention';
      }
    | {
        type: 'custom';
        iconString: 'avatar';
        profilePicture: string;
      }
  );

export type ContentTypesFlagProps = StandardFlagProps | CustomFlagProps;

export type AIAgentActionType = 'card' | 'diagram';

export type ConfluenceSidebarState = {
  isOpen: boolean;
  panelId:
    | 'detailsPanel'
    | 'commentsPanel'
    | 'ai-dynamic-surface'
    | 'ai-mate-container'
    | undefined;
};

export type OpenCrossFlowPayload = Parameters<
  Extract<CrossFlowContextType, { isEnabled: true }>['apiV1']['open']
>[0];

export type OpenCrossFlowHandler = (
  payload: OpenCrossFlowPayload,
) => Promise<OpenCrossFlowResult>;

// Offsets for the bottom and middle of a dialog toggle button used to calculate the position of the ai dialog
// Offsets are relative to the whiteboard container top and right edges
export type AIAgentDialogMountPoint = {
  toggleBottom: number;
  toggleMiddle: number;
};

export type DocumentTransformer<T> = {
  format: DocumentTransformerFormat;
  transform: DocumentTransformerCallback<T>;
};

export type FeedbackCollectorFeatureName =
  | 'Template Picker'
  | 'AI Generate Stickies'
  | 'Miro Importer'
  | 'Mural Importer'
  | 'AI Clustering'
  | 'AI Teammate'
  | 'Large Boards';

// Any new feature level feedback should go through here :)
type AdditionalFeedbackCollectorFields = {
  'Whiteboards Feature': FeedbackCollectorFeatureName;
};

export type GlobalFeedbackCollectorComponentProps = {
  onClose: () => void;
  onCloseComplete: () => void;
  feedbackTitle?: string;
  summaryPlaceholder?: string;
  // Additional metadata to be attached to the feedback context
  // NOTE: made mandatory to ensure we always have feature-level metadata attached to WBs feedback
  additionalFields: AdditionalFeedbackCollectorFields;

  // Excluded fields from CFE type (kept for reference)
  // ❌ feedbackSource: FeedbackSource; - Hardcoded to WHITEBOARD in CFE
  // ❌ feedbackTitleDetails?: React.ReactChild; - JSX which we ignore
  // ❌ showTypeField?: boolean; - Overwritten to true in CFE
  // ❌ contentType?: string; - Hardcoded to WHITEBOARD in CFE
};

export type PublicLink = {
  // Is this whiteboard a public link
  isEnabled: boolean;
  // Is it anonymously editable
  isEditEnabled: boolean;
  // Method to call to refresh the values of the public link
  refetch: () => void;
};

export type WhiteboardLockoutState =
  | {
      isLockoutSet: true;
      message?: string;
    }
  | {
      isLockoutSet: false;
    };

export interface BaseContentAPI<TokenGeneric> {
  sessionData: RawWhiteboardSessionData;

  goToContent: (id: ContentId | BoardId, query?: WhiteboardQueryParams) => void;
  goToContainer: (id: string) => void;
  goToHome: VoidFunction;

  onTokenInit: (handler: (context: TokenGeneric | undefined) => void) => void;
  onTokenChange: (handler: (context: TokenGeneric | undefined) => void) => void;
  preloadToken: (id?: BoardId) => Promise<void>;
  preloadContent?: (id?: ContentId) => Promise<void>;
  getToken: (id?: BoardId) => Promise<TokenGeneric | undefined>;
  refreshToken?: (id?: BoardId) => Promise<void>;

  onContainerInit: (handler: (context: FrameContainer) => void) => void;
  onContainerChange: (handler: (context: FrameContainer) => void) => void;

  onRouteChange?: (handler: (route: string) => void) => void;

  onContentLoadSucceeded?: (attributes: Record<string, any>) => void;
  onContentLoadFailed?: (error: Error, attributes: Record<string, any>) => void;
  onContentLoadAborted?: (reason: string, attributes: Record<string, any>) => void;
  setNavigationLockoutState?: (lockoutState: WhiteboardLockoutState) => void;

  isFeatureActive?: (name: string) => boolean;
  platformFeatureFlagResolver?: (name: string) => boolean;

  updateCollaborators?: (collaborators: Collaborators) => void;
  updateMainActions?: (items: MainActions) => void;
  updateMoreActions?: (items: MoreActions) => void;
  updateBoardStatus?: (status: BoardStatus) => void;

  /**
   * Asks Confluence Frontend to expand the whiteboard iframe to fill the browser tab
   * (host applies layout/CSS). Used for slides presenter “tab” layout instead of the Fullscreen API.
   */
  setTabFullscreen?: (enabled: boolean) => void;

  /**
   * Used to register a callback in Confluence that sets the active
   * value in Canvas's onboarding state to true to indicate that Canvas can
   * start its onboarding flow. This is needed because the onboarding eligibility
   * and timing logic lives in Confluence.
   *
   * There are also now multiple onboarding entrypoints (Beta, GA "Creator" and GA "Viewer")
   * so it needs to accept an optional initial screen
   *
   * @param callback A callback that's meant to accept an initial onboarding screen and
   * trigger onboarding. Our implementation sets `state.active` to true and `state.screen`
   * to `initialSpotlightScreen` in the onboarding state
   */
  registerStartOnboardingCallback?: (callback: OnboardingStartHandler) => void;

  /**
   * Called whenever the onboarding spotlight screen changes, to allow Confluence to
   * take action (e.g., fire an analytics event, display confetti, etc)
   * @param screen The spotlight screen name that was just rendered
   */
  onOnboardingChange?: (screen: SpotlightScreen) => void;

  /**
   * This is intended to be called once the whiteboards onboarding flow has
   * finished in Canvas. It calls a function that's been registered in Confluence
   * to handle any cleanup needed to end the overall onboarding tour.
   *
   * @param screen A string representing the name/identifier of the onboarding screen
   * that the flow finished on
   */
  onOnboardingEnd?: (screen: string) => void;

  onOrgIdChange?: (handler: (id: string) => void) => void;

  getOrgId?: () => string | undefined;

  publishDatabaseReferencingContent?: (selectionIds: string[] | undefined) => void;

  /**
   * Asks CFE to render paywall CTA with provided options
   * CFE will return a promise that is resolved when the CTA closes
   */
  showEditionUpgradeCTA?: (props: EditionUpgradeCTAProps) => Promise<void>;
  closeEditionUpgradeCTA?: () => void;

  /**
   * Asks CFE to open/close the global feedback collector
   */
  showFeedbackCollectorModal?: (props: GlobalFeedbackCollectorComponentProps) => void;
  closeFeedbackCollectorModal?: () => void;

  /***
   * Used to keep the theme state in sync between Canvas and Confluence
   */
  onThemeChange?: (handler: (theme: Partial<ThemeState>) => void) => void;
  getCurrentTheme?: () => Partial<ThemeState> | undefined;

  /***
   * Used to tell confluence that our app is ready to be shown
   */
  onThemeLoaded?: VoidFunction;
  /**
   * Generic method to trigger one of many tasks
   */
  triggerIntegration: (args: {
    id: string;
    onSuccess: (args: unknown) => void;
    onFailure?: (args: unknown) => void;
    otherProps: any;
  }) => Promise<void>;
  /**
   * Returns if a given task is available and ready to use
   */
  checkIntegration: (args: { id: string }) => boolean;

  onOnboardingReady?: (eligibleInitialScreens: SpotlightScreenType[]) => void;

  /**
   * Asks CFE to open/close a modal
   */
  showWhiteboardInternalModal?: (
    config: WhiteboardInternalModalConfiguration,
  ) => Promise<void>;
  closeWhiteboardInternalModal?: () => void;

  /**
   * Called in canvas to register the events in Confluence Frontend
   */
  registerEventHandler?: (
    event: ConfluenceEvent,
    handler: ConfluenceEventHandler,
  ) => void;

  /**
   * Trigger events in Confluence Frontend
   */
  triggerEvent?: (event: ConfluenceEvent) => void;

  /**
   * Called in canvas to unregister an event in Confluence Frontend
   */
  unregisterEventHandler?: (event: ConfluenceEvent) => void;

  /**
   * **IMPORTANT:** The content API is accessible to public (anonymous) users.
   * Consider whether the transformer being registered should be restricted to
   * users who have at least read access to the content.
   *
   * @description
   * Called in canvas when board data has been loaded and ECS has been booted
   * up. Used to register a callback that enables a caller in Confluence to
   * download a transformed document (e.g. in WDF) on demand.
   *
   * May be called once for each supported transformer, e.g. we may look into
   * supporting image previews or other formats for extensibility in the future.
   *
   * @example
   * Usage in Confluence:
   * ```ts
   * // whiteboard-bridge-api-builder.ts
   * build(): WhiteboardBridgeAPI {
   *   return {
   *     // ...
   *     registerDocumentTransformerCallback: (transformer) => {
   *       // assign `transformer` to a method in the service to be called later
   *       this.services.transformerService.registerDocumentTransformerCallback(transformer);
   *     }
   *     // ...
   *   }
   * }
   *
   * // file to invoke transformer in
   * const transformer = this.services.transformerService.get('WDF');
   * const { transformedDoc: wDoc } = transformer.transform();
   * ```
   */
  registerDocumentTransformerCallback?: (
    transformer: DocumentTransformer<unknown>,
  ) => void;

  /**
   * This should be called when fetching the current loaded version name (will return null on latest version)
   * @returns {string | null} The name of the current version, as it should be shown in CFE
   */
  getVersionName?: () => string;

  /**
   * Is this request coming from an external Confluence view
   * aka /wiki/external/<external-board-id>
   */
  isExternalShareRequest?: boolean;

  /**
   * Fetches metadata about the state of the externally shared public link
   * @returns {PublicLink} The public link state
   */
  getPublicLink?: () => PublicLink;
  /**
   * Refreshes the metadata of the public link
   */
  refetchPublicLink?: () => void;

  /**
   * Fetches the title of the current content
   */
  getTitle?: () => string | null | undefined;

  /**
   * Fetches the creator id of the current content
   */
  getContentCreatorId?: () => string | null | undefined;

  /**
   * Collection of methods for collaborating with Confluence regarding the AI Agent
   */

  /**
   *
   * Registers a handler in canvas to handle a `toggleAIAgent` event from within CFE
   */
  registerAIAgentToggleHandler?: (
    handler: (properties?: { selectedAction: AIAgentActionType }) => void,
  ) => void;

  /**
   * Method designed for CFE to use in order to toggle the AI Agent dialog component based on it's current state.
   * Internally calls the registered handler in canvas
   */
  toggleAIAgentDialog?: (properties?: { selectedAction: AIAgentActionType }) => void;

  /**
   * AI Agent bridge API method used to toggle the dynamic ai right panel based on it's current state
   */
  toggleAIAgentRightPanel?: () => void;

  /**
   * Callback handler for registering available AI actions with the dynamic ai right panel
   */
  onSmartActionsProvider?: (handler: () => AIAgentActionType[]) => void;

  /**
   * Method for Confluence to fetch registered available smart AI actions from confluence
   */
  updateSmartActions?: (actions: AIAgentActionType[]) => void;

  /**
   * Method to notify Confluence of changes in the state of the AI Agent dialog component
   */
  updateAIAgentDialogState?: (state: { isOpen: boolean }) => void;

  /**
   * Fetches the current position of the AI Agent dialog component used for a toggle.
   * Used to calculate dialog position in container
   */
  getAIAgentDialogMountPoint?: () => AIAgentDialogMountPoint;

  /**
   * Method to register a callback to be notified of changes in the state of the Confluence sidebar
   */
  onConfluenceSidebarStateChange?: (
    handler: (state: ConfluenceSidebarState) => void,
  ) => void;

  /**
   * Returns the current state of the Confluence sidebar.
   */
  getConfluenceSidebarState?: () => ConfluenceSidebarState;
}

/**
 * A handler for resolving a comment thread.
 * Note: If 'all' is passed in, then all comment threads will be resolved.
 */
export type ResolveCommentHandler = (threadKey: ResolveCommentThreadKey) => void;

/**
 * Commands dispatched from Confluence to Canvas over the slides
 * {@link SlidesContentTypeEventService} bridge.
 */
export interface SlidesContentTypeExportCommand {
  type: 'export';
  payload: { format: string };
}

export type SlidesContentTypeCommand = SlidesContentTypeExportCommand;

export type SlidesContentTypeCommandType = SlidesContentTypeCommand['type'];

export type SlidesContentTypeCommandHandler<
  T extends SlidesContentTypeCommandType = SlidesContentTypeCommandType,
> = (command: Extract<SlidesContentTypeCommand, { type: T }>) => void;

/**
 * Slides-scoped event service returned by
 * `contentTypeAPI.slides.mountEventService()`. Mirrors the existing
 * `whiteboards.mountEventService()` slot on the AFM side.
 */
export interface SlidesContentTypeEventService {
  registerHandler: <T extends SlidesContentTypeCommandType>(
    type: T,
    handler: SlidesContentTypeCommandHandler<T>,
  ) => void;
  unregisterHandler: (type: SlidesContentTypeCommandType) => void;
}
export type ResolveCommentThreadKey = 'all' | ThreadKey;
export type ThreadKey = string;

/**
 * This is a refactor of the original `BaseContentAPI` type to be more abstracted.
 * NOTE: Both databases and whiteboards use this type, so be careful when changing it.
 * The CFE counterpart can be found in the `@confluence/content-types-bridge`
 * package in atlassian-frontend-monorepo.
 * @see
 */
export interface BaseContentAPIv2 {
  /**
   * Methods related to triggering functionality within Confluence
   * Be sure to check @confluence/content-types-bridge in CFE for the
   * implementation of these methods
   * @see {@link https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/confluence/next/packages/content-types-bridge/src/content-types-shell-types.ts#confluence/next/packages/content-types-bridge/src/content-types-shell-types.ts-207}
   */
  confluenceAPI?: {
    shell?: {
      /**
       * A hook for the content frame to update the collaborators
       */
      updateCollaborators?: (collaborators: Collaborators) => void;
      /**
       * A hook for the content frame to update header actions
       */
      updateMainActions?: (actions: MainActions) => void;
      /**
       * A hook for the content frame to update the board status
       */
      updateBoardStatus?: (status: BoardStatus) => void;
      /**
       * A hook for the content frame to update header more actions
       */
      updateMoreActions?: (items: MoreActions) => void;
      /**
       * A hook for the content frame to update the title
       */
      updateTitle?: (title: string) => void;
      /**
       * A hook to check if feature gate is active
       */
      isFeatureActive?: (featureGateName: string) => boolean;
      /**
       * A hook for the content frame to register a handler that starts presentation mode.
       * Called by the slides iframe to allow the Confluence header Present button to trigger it.
       *
       * @returns An unsubscribe function to remove the handler.
       */
      onStartPresent?: (handler: () => void) => () => void;
      /**
       * Updates whether the Confluence header Present control is interactive while
       * a start-present handler remains registered.
       */
      setPresentSlidesEnabled?: (enabled: boolean) => void;
    };
    /**
     * Methods related to showing and closing the (right-hand) sidebar.
     */
    sidebar?: {
      /**
       * Called when the state of the sidebar changes
       */
      onConfluenceSidebarStateChange?: (
        callback: (state: ConfluenceSidebarState) => void,
      ) => void;
      /**
       * Shows the comments panel if not already open
       */
      showCommentsPanel?: (threadId?: string) => void;
      /**
       * Shows the details panel
       */
      showDetailsPanel?: () => void;
      /**
       * Hides the comments panel
       */
      hideCommentsPanel?: () => void;
      /**
       * Hides the details panel
       */
      hideDetailsPanel?: () => void;
      /**
       * Hides all external drawer panels
       * Currently doesn't actually exist on the frame API, but we'll fake it for now.
       */
      hideExternalDrawerPanels?: () => void;
    };
    /**
     * Methods related to showing and hiding the (left-hand) side navigation panel.
     */
    sideNav?: {
      /**
       * Toggles the open/closed state of the side navigation panel
       */
      toggleSideNav?: () => void;
      /**
       * Expands the side navigation panel
       */
      expandSideNav?: () => void;
      /**
       * Collapses the side navigation panel
       */
      collapseSideNav?: () => void;
      /**
       * Returns the current state of the side navigation panel
       */
      getIsSideNavCollapsed?: () => boolean;
    };
    comments?: {
      /**
       * Called in canvas to register the reopenResolvedCommentCallback in the resolved comments service
       */
      registerOnReopenResolvedComment?: (handler: ResolvedCommentHandler) => void;
      /**
       * Click event on a comment in the comments panel
       */
      onCommentsPanelSelectThread?: (callback: (threadId: string) => void) => void;
      /**
       * Selects a comment thread in the comments panel
       */
      selectCommentThread?: (threadId?: string) => void;
      /**
       * Called in canvas to register the onDeleteComment callback
       */
      registerOnDeleteComment?: (callback: (threadId: string) => void) => void;
      /**
       * Called in canvas to register the onUpdateComment callback
       */
      registerOnUpdateComment?: (
        callback: (threadId: string, actionType?: 'reply' | 'edit') => void,
      ) => void;
      /**
       * Triggers a refresh of the comments panel
       */
      triggerCommentPanelRefresh?: () => Promise<void>;
      /**
       * Called in canvas to register the onResolveComment callback
       */
      registerOnResolveComment?: (callback: ResolveCommentHandler) => void;
      /**
       * Called in canvas to register the onReaction update callback
       */
      registerOnUpdateReaction?: (
        callback: (
          threadId: string,
          emojiId: string,
          action: 'add' | 'delete',
        ) => Promise<void>,
      ) => void;
      /**
       * Returns whether reactions are enabled for the current site.
       */
      getIsReactionsEnabled?: () => boolean;
      /**
       * Triggers a notification to the host app that comments have been marked as read or unread
       */
      triggerReadStatusComment?: (commentIds: string[], isRead: boolean) => void;
      /**
       * Called in canvas to register the onReadStatusComment callback
       */
      registerOnReadStatusComment?: (
        callback: (commentIds: string[], isRead: boolean) => void,
      ) => void;
      /**
       * Gives Confluence the `commentId` (i.e. the inline marker ref) of every comment
       * element on the canvas. Confluence uses it to show threads with no comment
       * element as "Deleted content". Each call replaces the previous list.
       */
      syncActiveCommentMarkers?: (markerRefs: string[]) => void;
    };
    modals?: {
      /**
       * Opens the bulk import modal for importing from a specific source
       */
      openBulkImportModalToSpecificSource?: (source: string) => void;
      /**
       * Sends a positive feedback event to Confluence
       */
      sendBulkImportFeedbackPositive?: () => void;
      /**
       * Checks if the user is eligible for providing feedback on bulk imports
       */
      getIsBulkImportFeedbackEligible?: () => boolean;
      /**
       * Disables the bulk import feedback collector and associated UI elements for the user
       */
      setBulkImportFeedbackIneligible?: () => void;
      /**
       * Shows the bulk import feedback modal (CFE) if not already shown
       */
      showBulkImportFeedbackModal?: () => void;
      /**
       * Called in canvas to register the onBulkImportFeedbackModalSubmitted callback
       */
      registerOnBulkImportFeedbackModalSubmitted?: (callback: () => void) => void;
      /**
       * Shows a flag
       */
      showFlag?: (props: ContentTypesFlagProps) => void;
      /**
       * Hides a flag
       */
      hideFlag?: (flagId: string) => void;
    };
    /**
     * Cross-product flow APIs exposed by Confluence to Canvas.
     */
    crossFlow?: {
      /**
       * Starts a cross-product flow from Canvas (currently supports Jira Product Discovery only).
       */
      openCrossFlow?: OpenCrossFlowHandler;
    };
    /**
     * Onboarding / activation nudges owned by Confluence. Currently just the
     * TWCG-2055 "Flag to Share" progress signal.
     *
     * The CFE counterpart lives in the `@confluence/content-types-bridge`
     * package in atlassian-frontend-monorepo and must add the matching
     * implementation before this signal has any effect.
     */
    onboarding?: {
      /**
       * Reports content-creation progress so Confluence can evaluate whether to
       * show the Flag to Share nudge. Optional/no-op when unset — zero behaviour
       * change if Confluence never binds it.
       */
      notifyShareCriteriaProgress?: (progress: ShareCriteriaProgress) => void;
    };
  };

  /**
   * Per-content-type event service bridges provided by Confluence (AFM). Each
   * product key (`whiteboards`, `slides`, …) exposes its own
   * `mountEventService()` so commands dispatched from Confluence are routed to
   * the matching content type.
   *
   * Canvas subscribes via {@link SlidesContentTypeEventService.registerHandler}.
   *
   * @see {@link https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/confluence/next/packages/content-types-bridge/}
   */
  contentTypeAPI?: {
    slides?: {
      mountEventService?: () => SlidesContentTypeEventService | undefined;
    };
  };
}

/**
 * The content API, passed through from Confluence, as-is, without any form of
 * transformation or wrapping added to it
 *
 * Avoid using the Raw content API as much as possible as it means that data
 * coming from Confluence Frontend will need to be converted before being used
 * by the rest of the app - Either that or raw-types from Confluence Frontend
 * end up being leaked into Canvas.
 */
export type RawContentAPI = BaseContentAPI<RawFrameToken> & BaseContentAPIv2;

/**
 * A version of {@link RawContentAPI} that has been wrapped to output versions
 * of the bridge API that are more meaningful to our app. Use
 * this type whenever possible instead of {@link RawContentAPI}.
 */
export type ContentAPI = BaseContentAPI<FrameToken> & BaseContentAPIv2;

export type ARIString = string;
export type JWTString = string;
export type Edition = 'free' | 'standard' | 'premium';

/**
 * Expected data security policy type shape.
 */
export type DataSecurityPolicy = {
  [DataSecurityPolicyAction.pageExport]?: boolean;
  [DataSecurityPolicyAction.attachmentDownload]?: boolean;
};
/**
 * Expected policy type shape.
 */
export type Policies = {
  dataSecurityPolicy: DataSecurityPolicy;
};
/**
 * DataSecurityPolicy with all keys optional.
 */
export type DataSecurityPolicyBody = PartialDeep<DataSecurityPolicy>;
/**
 * Policies with all keys optional.
 */
export type PoliciesBody = PartialDeep<{
  dataSecurityPolicy: DataSecurityPolicyBody;
}>;

export enum DataSecurityPolicyAction {
  pageExport = 'pageExport',
  attachmentDownload = 'attachmentDownload',
}

export interface RawFrameToken {
  container: ARIString;
  board: ARIString;
  accessToken: JWTString;
  mediaToken: JWTString;
  mediaBaseUrl: string;
  mediaCollectionId: string;
  /**
   * undefined is technically not a valid value. We leave this as undefined to
   * protect against cases where Confluence Frontend changes the key (since we
   * don't have contract tests).
   */
  features?: EntitlementFeatureBody;
  /**
   * edition is only to be used for the purpose of analytics and, hence, only
   * appears as edition in the RawFrameToken. In the transformed FrameToken,
   * which is the version that should be used throughout the app, it is named
   * editionOnlyUseForAnalytics.
   */
  edition?: Edition;

  /**
   * Similar to the features field, we have to leave this undefined since there
   * aren't contract tests with the Confluence Frontend
   */
  policies?: PoliciesBody;
}

/**
 * Represents a version of {@link RawFrameToken} that has been transformed into
 * a more Canvas-developer friendly version of the original frame token payload
 * @privateRemark Similar to how we convert ARI strings to ARI objects as soon
 * as we receive a payload from a network request, we try convert the token to
 * something more meaningful to avoid unnecessary duplication of transformations
 * anywhere else in our code
 */
export type FrameToken = Omit<RawFrameToken, 'features' | 'edition' | 'policies'> & {
  features?: PaywallFeatureStateFromEntitlement;
  dataSecurityPolicy?: DataSecurityPolicy;
  editionOnlyUseForAnalytics?: Edition;
};

/**
 * State that we store in the frame valtio state
 * @privateRemark We store paywall state outside the frame token state which is
 * why we omit 'features'
 */
export type FrameState = Omit<FrameToken, 'features'>;

export interface FrameContext {
  data: FrameState;
}

export type ContentAPIFactory = (rawContentAPI: unknown) => ContentAPI | undefined;

export interface ContentWindowApi {
  title: string;
  href: string;
  isTopLevel: boolean;
  contentAPI: ContentAPI | undefined;
  reload: () => void;
  assignHref?: (href: string) => void;
  dispatchKeyboardEvent: (event: KeyboardEvent) => void;
}

export type RawTokenHandler = (context: RawFrameToken | undefined) => void;
export type ContainerHandler = (context: FrameContainer) => void;
export type RouteHandler = (route: string) => void;
